export type ColorMode = 'light' | 'dark'

/** Perceived-luminance check for Office theme colors ("#RRGGBB" or "RRGGBB"). */
function isDarkColor(color?: string): boolean {
  if (!color) return false
  const hex = color.replace('#', '')
  if (hex.length < 6) return false
  const r = parseInt(hex.slice(0, 2), 16)
  const g = parseInt(hex.slice(2, 4), 16)
  const b = parseInt(hex.slice(4, 6), 16)
  if ([r, g, b].some(Number.isNaN)) return false
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5
}

/**
 * Resolve the color mode from the Office host theme. Newer hosts expose
 * `officeTheme.isDarkTheme`; older ones only the theme colors, so fall back to
 * the luminance of the body background, then to the OS preference.
 */
export function detectColorMode(): ColorMode {
  const theme = (typeof Office !== 'undefined' ? Office?.context?.officeTheme : undefined) as
    | (Office.OfficeTheme & { isDarkTheme?: boolean })
    | undefined
  if (typeof theme?.isDarkTheme === 'boolean') return theme.isDarkTheme ? 'dark' : 'light'
  if (theme?.bodyBackgroundColor) return isDarkColor(theme.bodyBackgroundColor) ? 'dark' : 'light'
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
    return 'dark'
  }
  return 'light'
}

/** Scope the agent UI's markdown palette ([data-theme=...] in its style.css). */
export function applyColorMode(mode: ColorMode): void {
  document.documentElement.dataset.theme = mode
}

/**
 * Watch for host theme changes. `OfficeThemeChanged` only exists in newer
 * requirement sets, so it's feature-detected; the media query covers the rest.
 * Returns a cleanup function.
 */
export function watchColorMode(onChange: (mode: ColorMode) => void): () => void {
  const handler = () => onChange(detectColorMode())

  const doc = typeof Office !== 'undefined' ? Office?.context?.document : undefined
  const themeChanged = (Office?.EventType as Record<string, unknown> | undefined)?.OfficeThemeChanged as
    | Office.EventType
    | undefined
  if (doc?.addHandlerAsync && themeChanged) {
    try {
      doc.addHandlerAsync(themeChanged, handler)
    } catch {
      /* host does not support the event */
    }
  }

  const media = typeof window !== 'undefined' ? window.matchMedia?.('(prefers-color-scheme: dark)') : undefined
  media?.addEventListener?.('change', handler)

  return () => {
    media?.removeEventListener?.('change', handler)
    if (doc?.removeHandlerAsync && themeChanged) {
      try {
        doc.removeHandlerAsync(themeChanged, { handler })
      } catch {
        /* ignore */
      }
    }
  }
}
